import { useState } from 'react'

import { Content, Portal, Root, Trigger } from '@radix-ui/react-popover'
import { Command as CommandPrimitive } from 'cmdk'
import { X, ChevronDown } from 'lucide-react'

import { IconCheckboxChecked } from '~/components/ui/icons/checkbox-checked'
import { IconCheckboxEmpty } from '~/components/ui/icons/checkbox-empty'
import { cn } from '~/lib/utils'

interface Option {
  label: string
  value: string
}

interface SimplifiedMultiSelectProps {
  options: Option[]
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  placeholderIcon?: React.ReactNode
  hideSelectAll?: boolean
  isClearable?: boolean
  singleLine?: boolean
  maxCount?: number
  className?: string
  name?: string
  ariaLabel?: string
}

export const SimplifiedMultiSelect = ({
  options,
  value,
  onValueChange,
  placeholder = '選択してください',
  placeholderIcon,
  hideSelectAll = false,
  isClearable = false,
  singleLine = true,
  maxCount = 10,
  className,
  name,
  ariaLabel,
}: SimplifiedMultiSelectProps) => {
  const [isOpen, setIsOpen] = useState(false)

  const toggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue]
    onValueChange(newValue)
  }

  const toggleAll = () => {
    if (value.length === options.length) {
      onValueChange([])
    } else {
      onValueChange(options.map((o) => o.value))
    }
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onValueChange([])
  }

  const isAllSelected = value.length === options.length && options.length > 0

  return (
    <Root onOpenChange={setIsOpen} open={isOpen}>
      <Trigger asChild>
        <button
          aria-label={ariaLabel}
          className={cn(
            'relative h-[56px] w-full rounded border border-gray-300 text-left',
            'focus:border-[#09909E] focus:ring-2 focus:ring-[#09909E]',
            'transition-colors hover:border-gray-400',
            className,
          )}
          type="button"
        >
          <div className="flex h-full items-center">
            {value.length > 0 ? (
              <div
                className={cn(
                  'flex flex-1 items-center gap-2 p-4',
                  singleLine
                    ? 'scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent flex-nowrap overflow-x-auto'
                    : 'flex-wrap',
                )}
              >
                {value.slice(0, maxCount).map((val) => {
                  const option = options.find((o) => o.value === val)
                  if (!option) return null

                  return (
                    <div
                      className="flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded bg-[#E6E5E5] px-[6px] py-[3px] text-[85%]"
                      key={val}
                    >
                      <span>{option.label}</span>
                      <button
                        aria-label={`${option.label}を削除`}
                        className="rounded p-0.5 hover:bg-gray-300"
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleOption(val)
                        }}
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  )
                })}
                {value.length > maxCount && (
                  <div className="flex items-center text-[85%] text-gray-500">
                    +{value.length - maxCount} more
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-1 items-center p-4 text-gray-500">
                {placeholderIcon && (
                  <span className="mr-2 h-4 w-4">{placeholderIcon}</span>
                )}
                <span className="pt-1">{placeholder}</span>
              </div>
            )}

            {/* 右側のアイコン */}
            <div className="flex flex-shrink-0 items-center gap-1 bg-white pr-2">
              {isClearable && value.length > 0 && (
                <button
                  aria-label="全て削除"
                  className="rounded p-1 hover:bg-gray-200"
                  onClick={handleClear}
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
              <ChevronDown className="h-4 w-4 text-gray-500" />
            </div>
          </div>
        </button>
      </Trigger>

      <Portal>
        <Content
          className="z-[10100] w-[var(--radix-popover-trigger-width)] rounded border border-gray-200 bg-white shadow-lg"
          sideOffset={4}
        >
          <CommandPrimitive className="w-full">
            <CommandPrimitive.List className="max-h-64 overflow-y-auto">
              {/* 全選択オプション */}
              {!hideSelectAll && (
                <>
                  <CommandPrimitive.Item
                    className="flex cursor-pointer items-center gap-3 px-4 py-3 font-medium hover:bg-gray-100"
                    onSelect={toggleAll}
                  >
                    {isAllSelected ? (
                      <IconCheckboxChecked className="h-5 w-5 flex-shrink-0" />
                    ) : (
                      <IconCheckboxEmpty className="h-5 w-5 flex-shrink-0" />
                    )}
                    <span>全て選択</span>
                  </CommandPrimitive.Item>
                  <div className="my-1 h-px bg-gray-200" />
                </>
              )}

              {/* 個別オプション */}
              <CommandPrimitive.Group>
                {options.map((option) => {
                  const isSelected = value.includes(option.value)
                  return (
                    <CommandPrimitive.Item
                      className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-gray-100"
                      key={option.value}
                      onSelect={() => toggleOption(option.value)}
                      value={option.value}
                    >
                      {isSelected ? (
                        <IconCheckboxChecked className="h-5 w-5 flex-shrink-0" />
                      ) : (
                        <IconCheckboxEmpty className="h-5 w-5 flex-shrink-0" />
                      )}
                      <span>{option.label}</span>
                    </CommandPrimitive.Item>
                  )
                })}
              </CommandPrimitive.Group>
            </CommandPrimitive.List>
          </CommandPrimitive>
        </Content>
      </Portal>

      {/* Hidden input for form submission */}
      {name && value.length > 0 && (
        <input name={name} type="hidden" value={value.join(',')} />
      )}
    </Root>
  )
}
